/**
 * XR Assistant Panel — audio helpers.
 *
 * Small AudioContext-backed utilities extracted from XRAssistantPanel so
 * the main panel class can stay focused on UI / state. Pure AudioContext
 * consumers — no DOM or Babylon references — so they can be unit-tested
 * or swapped in isolation.
 */

/**
 * Resume a suspended AudioContext in response to a user gesture. Browsers
 * suspend `new AudioContext()` until a gesture and silently drop any
 * `start()` / `decodeAudioData()` calls made while suspended. Call this
 * from any entry point that originates in a user gesture (tap, button).
 */
export function ensureAudioContextRunning(ctx: AudioContext | null): void {
    if (!ctx) return;
    if (ctx.state === 'suspended') {
        void ctx.resume();
    }
}

/** Play a short confirmation tone via an oscillator. */
export function playTone(
    ctx: AudioContext | null,
    frequency: number,
    durationMs: number,
    type: OscillatorType = 'sine',
): void {
    if (!ctx) return;
    ensureAudioContextRunning(ctx);
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = frequency;
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + durationMs / 1000);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + durationMs / 1000);
}

/** Rising two-tone chirp — "recording started". */
export function playStartTone(ctx: AudioContext | null): void {
    playTone(ctx, 440, 120);
    setTimeout(() => playTone(ctx, 660, 120), 80);
}

/** Falling two-tone chirp — "recording stopped / submitted". */
export function playStopTone(ctx: AudioContext | null): void {
    playTone(ctx, 660, 120);
    setTimeout(() => playTone(ctx, 440, 150), 80);
}
